<template>
  <article class="contents-content">
    <div class="contents-content__content">
      <div class="content-in-area">

        <!-- 변경 사항 확인 팝업 -->
        <div v-if="diffDialogShow" class="overlay">
          <div class="dialog-box">
            <div class="dialog-title">엑셀 적용 시 변경사항</div>
            <div class="dialog-subtitle">
              기존의 데이터가 아래와 같이 변경되며, 적용시 이전 데이터를 덮어씁니다
            </div>
            <div class="dialog-content">
              <!-- 충돌 차 목록 -->
              <div v-if="diffDialogContent.conflicts.length > 0">
                <div class="change-section-title"><strong>[충돌]</strong></div>
                <div v-for="(car, idx) in diffDialogContent.conflicts" :key="'new-' + idx" class="change-line">
                  · {{ car }}
                </div>
              </div>

              <!-- 추가된 차 목록 -->
              <div v-if="diffDialogContent.newCars.length > 0">
                <div class="change-section-title"><strong>[추가된 차]</strong></div>
                <div v-for="(car, idx) in diffDialogContent.newCars" :key="'new-' + idx" class="change-line">
                  · {{ car }}
                </div>
              </div>

              <!-- 제거된 차 목록 -->
              <div v-if="diffDialogContent.deleteCars.length > 0" class="mt-4">
                <div class="change-section-title"><strong>[제거된 차]</strong></div>
                <div v-for="(car, idx) in diffDialogContent.deleteCars" :key="'del-' + idx" class="change-line">
                  · {{ car }}
                </div>
              </div>

              <!-- 변경된 차 목록 -->
              <div v-if="diffDialogContent.changeCars.length > 0" class="mt-4">
                <div class="change-section-title"><strong>[변경된 차]</strong></div>
                <div
                  v-for="(item, idx) in diffDialogContent.changeCars"
                  :key="'chg-' + idx"
                  class="change-line"
                >
                  · {{ item.carNumber }}
                  <ul class="ml-3">
                    <li v-for="(c, i) in item.changes" :key="'chg-' + idx + '-' + i">{{ c }}</li>
                  </ul>
                </div>
              </div>

              <!-- 변경사항이 없을 경우 -->
              <div v-if="diffDialogContent.conflicts.length === 0 && diffDialogContent.newCars.length === 0 && diffDialogContent.deleteCars.length === 0 && diffDialogContent.changeCars.length === 0">
                <div class="change-line">변경사항이 없습니다.</div>
              </div>
            </div>
            <div class="dialog-buttons">
              <template v-if="diffDialogContent.conflicts.length === 0 && diffDialogContent.newCars.length === 0 && diffDialogContent.deleteCars.length === 0 && diffDialogContent.changeCars.length === 0">
                <v-btn color="primary" @click="onButtonDialogCancel">확인</v-btn>
              </template>
              <template v-else>
                <v-btn outlined color="grey" @click="onButtonDialogCancel">취소</v-btn>
                <v-btn color="primary" @click="onButtonDialogConfirm">적용</v-btn>
              </template>
            </div>
          </div>
        </div>

        <v-dialog v-model="showDeleteDialog" max-width="400">
          <v-card>
            <v-card-title class="headline">차량 정보 제거</v-card-title>
            <v-card-text>정말 해당 차량 정보를 제거하시겠습니까?</v-card-text>
            <v-card-actions class="justify-end">
              <v-btn text @click="showDeleteDialog = false">취소</v-btn>
              <v-btn color="red" text @click="onDelete">제거</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <div class="d-flex align-center" style="height:50pt; padding:0 16px;">
          <!-- 왼쪽 그룹: 텍스트필드 + 조회 -->
          <div class="d-flex align-center">
            <v-text-field
              v-model="searchText"
              label="검색 문자열"
              dense
              hide-details
              style="height:100%; max-width:200px;"
            ></v-text-field>
          </div>

          <!-- 오른쪽 그룹: 엑셀 입력 → 엑셀 출력 → 등록 -->
          <div class="d-flex align-center ml-auto">
            <v-btn class="mr-2" style="height:30pt" height="100%" @click="onExcelImport">엑셀 입력</v-btn>
            <v-btn class="mr-2" style="height:30pt" height="100%" @click="onExcelExport">엑셀 출력</v-btn>
            <v-btn v-if="car.carNumber !== '' && !carExists" color="secondary" style="height:30pt" height="100%" @click="onRegister">차량 추가</v-btn>
            <v-btn v-else color="primary" style="height:30pt" height="100%" :disabled="car.carNumber === ''" @click="onRegister">정보 저장</v-btn>
          </div>
        </div>
        <v-container>
          <v-row>
            <nav class="content-left-menu" style="width: 400px">
              <transition name="fade">
                <div v-if="carList && carList.length > 0">
                  <ul class="content-menu-list" ref="contentMenu" >
                    <li
                      v-for="item in filteredCarList"
                      :key="item.carNumber"
                      :class="['content-menu__item', {'is--active' : item.carNumber === car.carNumber}]"
                      @click="setCurrentCar(item)"
                    >
                      <strong class="menu__item-main">
                        <div class="menu__item-expanded">
                          <!-- 상단: 차번호 · 차주이름 on left, 등록·수정 날짜 on right -->
                          <div class="menu__header d-flex justify-space-between">
                            <div class="menu__title">
                              <span class="car-number mr-2">{{ item.carNumber }}</span>
                              <span class="owner-name">{{ item.ownerName }}</span>
                            </div>
                            <div class="menu__dates text-right">
                              <div class="date-item" style="font-size: 8pt">등록: {{ item.createdDate }}</div>
                              <div class="date-item" style="font-size: 8pt">수정: {{ item.updatedDate }}</div>
                            </div>
                          </div>

                          <!-- 하단: 상태아이콘·문자열·이유 on left, 가족수·비고 on right -->
                          <div class="menu__body d-flex justify-space-between align-center">
                            <div v-if="getStatus(item).normal" class="d-flex align-center">
                              <v-icon small class="status-icon" style="color: #627ef8">mdi-circle</v-icon>
                              <span class="status-label">일반</span>
                            </div>
                            <div v-if="getStatus(item).black" class="d-flex align-center">
                              <v-icon small class="status-icon" style="color: black">mdi-circle</v-icon>
                              <span class="status-label">블랙</span>
                            </div>
                            <div v-if="getStatus(item).basement" class="d-flex align-center">
                              <v-icon small class="status-icon" style="color: orange">mdi-circle</v-icon>
                              <span class="status-label">지하</span>
                            </div>
                            <div v-if="getStatus(item).newbie" class="d-flex align-center">
                              <v-icon small class="status-icon" style="color: green">mdi-circle</v-icon>
                              <span class="status-label">새가족</span>
                            </div>
                            <div class="d-flex align-center">
                              <span v-if="item.relationships && item.relationships.length > 0" class="family-count">대리인 {{ item.relationships.length }}명</span>
                              <span v-if="item.ownerDetail" class="note ml-2">비고</span>
                            </div>
                          </div>
                        </div>
                      </strong>
                    </li>
                  </ul>
                </div>
                <p class="content--empty" v-if="carList == null || carList.length === 0">등록된 차량이 없습니다.</p>
              </transition>
            </nav>
            <div class="content--has-menu">
              <div>
                <v-form ref="form" v-if="car && car.carNumber !== ''">
                  <v-container fluid>
                    <!-- 차주 정보 -->
                    <fieldset class="detail-section">
                      <legend>차주 정보</legend>
                      <!-- 1. 차번호 -->
                      <v-row>
                        <v-col cols="10">
                          <v-text-field
                            v-model="car.carNumber"
                            label="차번호*"
                            outlined
                            dense
                            style="background-color: #ffffff"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="2">
                          <v-btn style="height: 30pt" height="100%" :disabled="!car.carNumber || !carExists" @click="showDeleteDialog = true">차량 제거</v-btn>
                        </v-col>
                      </v-row>

                      <!-- 2. 차주정보 + 사진보기 -->
                      <v-row>
                        <v-col cols="6">
                          <v-text-field
                            v-model="car.ownerName"
                            label="차주이름*"
                            outlined
                            dense
                            style="background-color: #ffffff"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model="car.ownerPhone"
                            label="차주전화번호*"
                            outlined
                            dense
                            style="background-color: #ffffff"
                            hide-details
                          />
                        </v-col>
                      </v-row>

                      <!-- 사유 -->
                      <v-textarea
                        v-model="car.ownerDetail"
                        label="비고"
                        auto-grow
                        dense
                        outlined
                        class="reason-field"
                        style="background-color: #ffffff"
                        hide-details
                      />
                    </fieldset>

                    <!-- 2. 대리인 -->
                    <fieldset class="detail-section">
                      <legend>대리인</legend>
                      <!-- 기존 대리인들 -->
                      <div
                        v-for="(item, idx) in car.relationships"
                        :key="idx"
                      >
                        <div class="relation-row">
                          <v-text-field
                            v-model="item.name"
                            label="이름"
                            outlined
                            dense
                            class="rel-field mr-3"
                            style="background-color: #ffffff"
                            hide-details
                          />
                          <v-text-field
                            v-model="item.number"
                            label="전화번호"
                            outlined
                            dense
                            class="rel-field mr-3"
                            style="background-color: #ffffff"
                            hide-details
                          />
                          <v-text-field
                            v-model="item.relationship"
                            label="관계"
                            outlined
                            dense
                            class="rel-field mr-3"
                            style="background-color: #ffffff"
                            hide-details
                          />

                          <v-btn icon @click="onRemoveRelation(idx)" density="compact">
                            <v-icon color="red">mdi-minus-circle</v-icon>
                          </v-btn>
                        </div>
                      </div>

                      <!-- 새 대리 추가 행 -->
                      <div
                        class="relation-row"
                        v-if="car.relationships.length < 5"
                      >
                        <v-text-field
                          v-model="newRelation.name"
                          label="이름"
                          outlined
                          dense
                          class="rel-field mr-3"
                          style="background-color: #ffffff"
                          hide-details
                        />
                        <v-text-field
                          v-model="newRelation.number"
                          label="전화번호"
                          outlined
                          dense
                          class="rel-field mr-3"
                          style="background-color: #ffffff"
                          hide-details
                        />
                        <v-text-field
                          v-model="newRelation.relationship"
                          label="관계"
                          outlined
                          dense
                          class="rel-field mr-3"
                          style="background-color: #ffffff"
                          hide-details
                        />

                        <v-btn icon class="add-btn" @click="onAddRelation" density="compact">
                          <v-icon color="blue">mdi-plus-circle</v-icon>
                        </v-btn>
                      </div>
                    </fieldset>

                    <!-- 3. 블랙리스트 -->
                    <fieldset class="detail-section">
                      <legend>블랙</legend>

                      <!-- 사진 영역 -->
                      <div class="photo-row">

                        <!-- 기존 사진 썸네일 -->
                        <div
                          v-for="(pic, idx) in this.car.black.pictures"
                          :key="idx"
                          class="photo-thumb"
                        >
                          <v-img :src="getImageUrl(pic)" cover class="thumb-img" />
                          <v-btn icon small class="remove-btn" @click="onRemovePhoto(idx)">
                            <v-icon color="red">mdi-minus-circle</v-icon>
                          </v-btn>
                        </div>
                        <!-- 빈 슬롯(추가 버튼) -->
                        <div
                          v-for="index in (5 - this.car.black.pictures.length)"
                          :key="car.black.pictures.length + index"
                          class="photo-add"
                          @click="onAddPhoto()"
                        >
                          <v-icon large>mdi-plus</v-icon>
                        </div>
                      </div>

                      <!-- 날짜 입력 -->
                      <div class="date-row">
                        <v-text-field
                          v-model="car.black.startDate"
                          label="등록날짜"
                          type="datetime-local"
                          step="1"
                          dense
                          outlined
                          class="date-field"
                          style="background-color: #ffffff"
                          hide-details
                          placeholder="yyyy-MM-dd HH:mm:ss"
                        />
                        <v-btn class="mr-5" @click="onNowBlackStartDate">지금</v-btn>
                        <span class="tilde">~</span>
                        <v-text-field
                          v-model="car.black.endDate"
                          label="해제날짜"
                          type="datetime-local"
                          step="1"
                          dense
                          outlined
                          class="date-field"
                          style="background-color: #ffffff"
                          hide-details
                          placeholder="yyyy-MM-dd HH:mm:ss"
                        />
                        <v-btn @click="onNowBlackEndDate">지금</v-btn>
                      </div>

                      <!-- 사유 -->
                      <v-textarea
                        v-model="car.black.reason"
                        label="등록사유"
                        auto-grow
                        dense
                        outlined
                        class="reason-field"
                        style="background-color: #ffffff"
                        hide-details
                      />
                    </fieldset>

                    <!-- 4. 지하주차장 이용 -->
                    <fieldset class="detail-section">
                      <legend>지하주차장</legend>

                      <!-- 날짜 입력 -->
                      <div class="date-row">
                        <v-text-field
                          v-model="car.basement.startDate"
                          label="등록날짜"
                          type="datetime-local"
                          step="1"
                          dense
                          outlined
                          class="date-field"
                          style="background-color: #ffffff"
                          hide-details
                          placeholder="yyyy-MM-dd HH:mm:ss"
                        />
                        <!-- 다음 버튼 -->
                        <v-btn class="mr-5" @click="onNowBasementStartDate">지금</v-btn>
                        <span class="tilde">~</span>
                        <v-text-field
                          v-model="car.basement.endDate"
                          label="해제날짜"
                          type="datetime-local"
                          step="1"
                          dense
                          outlined
                          class="date-field"
                          style="background-color: #ffffff"
                          hide-details
                          placeholder="yyyy-MM-dd HH:mm:ss"
                        />
                        <v-btn @click="onNowBasementEndDate">지금</v-btn>
                      </div>

                      <!-- 사유 -->
                      <v-textarea
                        v-model="car.basement.reason"
                        label="등록사유"
                        auto-grow
                        dense
                        outlined
                        class="reason-field"
                        style="background-color: #ffffff"
                        hide-details
                      />
                    </fieldset>

                    <!-- 5. 새가족 이용 -->
                    <fieldset class="detail-section">
                      <legend>새가족</legend>

                      <!-- 날짜 입력 -->
                      <div class="date-row">
                        <v-text-field
                          v-model="car.newbie.startDate"
                          label="등록날짜"
                          type="datetime-local"
                          step="1"
                          dense
                          outlined
                          class="date-field"
                          style="background-color: #ffffff"
                          hide-details
                          placeholder="yyyy-MM-dd HH:mm:ss"
                        />
                        <!-- 다음 버튼 -->
                        <v-btn class="mr-5" @click="onNowNewbieStartDate">지금</v-btn>
                        <span class="tilde">~</span>
                        <v-text-field
                          v-model="car.newbie.endDate"
                          label="해제날짜"
                          type="datetime-local"
                          step="1"
                          dense
                          outlined
                          class="date-field"
                          style="background-color: #ffffff"
                          hide-details
                          placeholder="yyyy-MM-dd HH:mm:ss"
                        />
                        <v-btn @click="onNowNewbieEndDate">지금</v-btn>
                      </div>

                      <!-- 비고 -->
                      <v-textarea
                        v-model="car.newbie.reason"
                        label="비고"
                        auto-grow
                        dense
                        outlined
                        class="reason-field"
                        style="background-color: #ffffff"
                        hide-details
                      />
                    </fieldset>
                  </v-container>
                </v-form>
                <p v-else class="content--empty">선택된 차량이 없습니다.</p>
              </div>
            </div>
          </v-row>
        </v-container>

        <!-- 3) 실제 파일 선택용 input (숨겨져 있음) -->
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="onHandleImageFile"
        />

        <!-- 3) 엑셀 파일 선택용 input (숨겨져 있음) -->
        <input
          ref="excelFileInput"
          type="file"
          accept=".xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          style="display: none"
          @change="onImportExcelFile"
        />
      </div>
    </div>
  </article>
</template>

<script>
import { car, image } from '@/api'
import eventBus from '@/utils/eventBus'
import * as XLSX from 'xlsx'
import XlsxPopulate from 'xlsx-populate'

export default {
  name: 'Car',
  components: {
    // Carousel
  },
  data: () => ({
    carList: null,
    carListReadTemp: null, // 엑셀 입력을 통해 읽은 데이터를 잠시 보관할 변수
    car: {
      carNumber: '',
      ownerName: '',
      ownerPhone: '',
      ownerDetail: '',
      relationships: [
        // {
        //   name: '',
        //   number: '',
        //   relationship: ''
        // }
      ],
      createdDate: '',
      updatedDate: '',
      black: null,
      // {
      //   startDate: ''
      //   endDate: '',
      //   reason: '',
      //   pictures: [''],
      // }
      basement: null,
      // {
      //   startDate: ''
      //   endDate: '',
      //   reason: '',
      // }
      newbie: null
      // {
      //   startDate: ''
      //   endDate: '',
      //   reason: '',
      // }
    },
    newRelation: {
      // name: '',
      // number: '',
      // relationship: ''
    },
    carBlackImageTarget: null,
    searchText: '',
    diffDialogShow: false,
    diffDialogContent: {},
    isVisibleDeleteButton: false,
    showDeleteDialog: false
  }),
  created () {
    this.fetchList()
  },
  computed: {
    carExists () {
      return this.carList.some(item => item.carNumber === this.car.carNumber)
    },
    filteredCarList () {
      if (!this.searchText || this.searchText.trim() === '') {
        return this.carList
      }

      const text = this.searchText.trim()

      return this.carList.filter(car => {
        const status = this.getStatus(car)

        // 특별 키워드 검색
        if (this.searchText === '지하' && status.basement) return true
        if (this.searchText === '블랙' && status.black) return true
        if (this.searchText === '새가족' && status.newbie) return true

        const match = [
          car.carNumber,
          car.ownerName,
          car.ownerPhone,
          car.ownerDetail,
          car.black?.reason,
          car.basement?.reason,
          car.newbie?.reason,
          ...(car.relationships || []).flatMap(r => [r.name, r.number, r.relationship])
        ].filter(Boolean).some(field => field.toLowerCase().includes(text))

        return match
      })
    }
  },
  methods: {
    async fetchList () {
      eventBus.$emit('showLoading')
      const allListResponse = await car.list_all() // 차량 목록
      eventBus.$emit('hideLoading')
      if (allListResponse.status !== 200) {
        eventBus.$emit('alert', { active: true, text: '데이터 로드 실패' })
        return
      }
      this.carList = allListResponse.data
    },
    setCurrentCar (item) {
      this.car = JSON.parse(JSON.stringify(item))
      if (this.car.black !== null) {
        this.car.black.startDate = this.parseYMDHM(item.black.startDate)
        this.car.black.endDate = this.parseYMDHM(item.black.endDate)
      }
      if (this.car.basement !== null) {
        this.car.basement.startDate = this.parseYMDHM(item.basement.startDate)
        this.car.basement.endDate = this.parseYMDHM(item.basement.endDate)
      }
      if (this.car.newbie !== null) {
        this.car.newbie.startDate = this.parseYMDHM(item.newbie.startDate)
        this.car.newbie.endDate = this.parseYMDHM(item.newbie.endDate)
      }
    },
    onExcelImport () {
      this.$refs.excelFileInput.click()
    },
    onExcelExport () {
      this.exportFromTemplate()
    },
    async onRegister () {
      eventBus.$emit('showLoading')
      const copyCar = JSON.parse(JSON.stringify(this.car))
      if (this.car.black !== null) {
        copyCar.black.startDate = this.formatYMDHM(this.car.black.startDate)
        copyCar.black.endDate = this.formatYMDHM(this.car.black.endDate)
        copyCar.black.pictures = this.car.black.pictures
        copyCar.black.reason = this.car.black.reason
      }
      if (this.car.basement !== null) {
        copyCar.basement.startDate = this.formatYMDHM(this.car.basement.startDate)
        copyCar.basement.endDate = this.formatYMDHM(this.car.basement.endDate)
        copyCar.basement.reason = this.car.basement.reason
      }
      if (this.car.newbie !== null) {
        copyCar.newbie.startDate = this.formatYMDHM(this.car.newbie.startDate)
        copyCar.newbie.endDate = this.formatYMDHM(this.car.newbie.endDate)
        copyCar.newbie.reason = this.car.newbie.reason
      }
      const { data } = await car.update(copyCar)
      eventBus.$emit('hideLoading')
      if (data === 'succeed') {
        const idx = this.carList.findIndex(item =>
          item.carNumber === this.car.carNumber
        )
        if (idx === -1) return
        this.carList.splice(idx, 1, copyCar)
        eventBus.$emit('alert', { active: true, text: '등록 되었습니다.' })
      } else {
        eventBus.$emit('alert', { active: true, text: '에러!' })
      }
    },
    async onDelete () {
      this.showDeleteDialog = false
      eventBus.$emit('showLoading')
      await car.delete(this.car.carNumber)
      eventBus.$emit('hideLoading')
      this.car = {
        carNumber: '',
        ownerName: '',
        ownerPhone: '',
        ownerDetail: '',
        relationships: [],
        createdDate: '',
        updatedDate: '',
        black: null,
        basement: null,
        newbie: null
      }
      await this.fetchList()
    },
    onAddRelation () {
      // 1) newRelation 을 얕은 복사해서 배열에 추가
      this.car.relationships.push({
        name: this.newRelation.name || '',
        number: this.newRelation.number || '',
        relationship: this.newRelation.relationship || ''
      })

      // 2) newRelation 초기화
      this.newRelation = { name: '', number: '', relationship: '' }
    },
    getImageUrl (imageName) {
      return image.getUrl(imageName)
    },
    onRemoveRelation (index) {
      this.car.relationships.splice(index, 1)
    },
    onAddPhoto () {
      this.$refs.fileInput.click()
    },
    onRemovePhoto (index) {
      this.car.black.pictures.splice(index, 1)
    },
    getStatus (item) {
      if (!item) return { normal: true, black: false, basement: false, newbie: false }
      const now = this.toTimestamp(this.formatYMDHM(Date.now())) + 1
      const bs = this.toTimestamp(item.black?.startDate)
      const be = this.toTimestamp(item.black?.endDate)
      const vs = this.toTimestamp(item.basement?.startDate)
      const ve = this.toTimestamp(item.basement?.endDate)
      const ns = this.toTimestamp(item.newbie?.startDate)
      const ne = this.toTimestamp(item.newbie?.endDate)

      const isBlack = bs && bs <= now && (!be || now <= be || be < bs)
      const isBasement = vs && vs <= now && (!ve || now <= ve)
      const isNewbie = ns && ns <= now && (!ne || now <= ne)
      const normal = !isBlack && !isBasement && !isNewbie
      return { normal: normal, black: isBlack, basement: isBasement, newbie: isNewbie }
    },
    toTimestamp (dateStr) {
      if (!dateStr) return null
      const parsed = new Date(dateStr.replace(/-/g, '/'))
      return parsed.getTime()
    },
    onNowBlackStartDate () {
      if (this.car.black == null) { this.car.black = { startDate: '', endDate: '', reason: '', pictures: [] } }
      this.car.black.startDate = new Date().toISOString().slice(0, 16)
    },
    onNowBlackEndDate () {
      if (this.car.black == null) { this.car.black = { startDate: '', endDate: '', reason: '', pictures: [] } }
      this.car.black.endDate = new Date().toISOString().slice(0, 16)
    },
    onNowBasementStartDate () {
      if (this.car.basement == null) { this.car.basement = { startDate: '', endDate: '', reason: '' } }
      this.car.basement.startDate = new Date().toISOString().slice(0, 16)
    },
    onNowBasementEndDate () {
      if (this.car.basement == null) { this.car.basement = { startDate: '', endDate: '', reason: '' } }
      this.car.basement.endDate = new Date().toISOString().slice(0, 16)
    },
    onNowNewbieStartDate () {
      if (this.car.newbie == null) { this.car.newbie = { startDate: '', endDate: '', reason: '' } }
      this.car.newbie.startDate = new Date().toISOString().slice(0, 16)
    },
    onNowNewbieEndDate () {
      if (this.car.newbie == null) { this.car.newbie = { startDate: '', endDate: '', reason: '' } }
      this.car.newbie.endDate = new Date().toISOString().slice(0, 16)
    },
    onButtonDialogCancel () {
      this.diffDialogShow = false
      this.diffDialogContent = {}
    },
    async onButtonDialogConfirm () {
      this.diffDialogShow = false
      this.diffDialogContent = {}
      eventBus.$emit('showLoading')
      await car.update_all({ cars: this.resolveCarListConflicts(this.carListReadTemp) })
      eventBus.$emit('hideLoading')
      await this.fetchList()
    },
    // Date 또는 ISO 문자열 → "yyyy-MM-dd HH:mm:ss"
    formatYMDHM (str) {
      if (str === null) return null
      const d = new Date(str)
      if (isNaN(d)) return ''
      const z = n => String(n).padStart(2, '0')
      return (
        `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}` +
        ` ${z(d.getHours())}:${z(d.getMinutes())}:${z(d.getSeconds())}`
      )
    },
    // "yyyy-MM-dd HH:mm:ss" → ISO 문자열 중 "yyyy-MM-ddThh:mm:ss" 부분
    parseYMDHM (val) {
      if (val === null) return null
      // "2025-05-30 14:05:09"
      const [datePart, timePart] = val.split(' ')
      if (!datePart || !timePart) return ''
      const [y, m, d] = datePart.split('-').map(Number)
      const [h, mi, s] = timePart.split(':').map(Number)
      const dt = new Date(y, m - 1, d, h, mi, s)
      if (isNaN(dt)) return ''
      // toISOString() → "2025-05-30T14:05:09.000Z"
      // substring(0,19) → "2025-05-30T14:05:09"
      return dt.toISOString().substring(0, 19)
    },

    /**
     * 파일이 선택되면 호출됩니다.
     * event.target.files 에서 첫 번째 파일을 꺼내 selectedFile에 저장합니다.
     */
    async onHandleImageFile (event) {
      const files = event.target.files
      if (!(files && files.length > 0)) return
      try {
        this.carBlackImageTarget = await this.resizeImageFile(files[0])
        await this.onProcessAddImage()
      } catch (err) {
        eventBus.$emit('alert', { active: true, text: '이미지 리사이즈 실패:' })
      }
      this.$refs.fileInput.value = ''
    },
    makeFileFormData (file) {
      const formData = new FormData()
      formData.append('sourceFile', file)
      return formData
    },
    async onProcessAddImage () {
      if (this.carBlackImageTarget == null) return
      const { data } = await image.create(this.makeFileFormData(this.carBlackImageTarget))
      if (this.car.black == null) { this.car.black = { startDate: '', endDate: '', reason: '', pictures: [] } }
      this.car.black.pictures.push(data.name)
    },

    /**
     * 주어진 이미지 파일을 가로·세로 최대 1024px로 비율 유지하여 축소한 뒤,
     * JPEG 형식의 새로운 File 객체로 반환합니다.
     *
     * @param {File} file - 입력 이미지 파일 (예: PNG, JPEG 등)
     * @param {number} maxSize - 축소할 최대 크기 (기본값: 1024)
     * @param {number} quality - JPEG 압축 품질 (0.0 ~ 1.0, 기본값: 0.8)
     * @returns {Promise<File>} - 축소된 JPEG File 객체
     */
    async resizeImageFile (file, maxSize = 1024, quality = 0.8) {
      // 1. File → HTMLImageElement 로딩
      function loadImageFromFile (inputFile) {
        return new Promise((resolve, reject) => {
          const url = URL.createObjectURL(inputFile)
          const img = new Image()
          img.onload = () => {
            URL.revokeObjectURL(url)
            resolve(img)
          }
          img.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error('이미지 로드 실패'))
          }
          img.src = url
        })
      }

      // 2. 원본(w, h)을 기준으로 최대 maxSize를 넘지 않도록 비율 계산
      function calcTargetSize (origW, origH, limit) {
        const ratio = Math.min(limit / origW, limit / origH, 1)
        return {
          width: Math.round(origW * ratio),
          height: Math.round(origH * ratio)
        }
      }

      // 3. HTMLImageElement → 캔버스에 그려 JPEG Blob 생성
      function imageToJpegBlob (imageEl, targetW, targetH, jpegQuality) {
        return new Promise((resolve) => {
          const canvas = document.createElement('canvas')
          canvas.width = targetW
          canvas.height = targetH
          const ctx = canvas.getContext('2d')
          ctx.drawImage(imageEl, 0, 0, targetW, targetH)
          canvas.toBlob(
            (blob) => {
              resolve(blob)
            },
            'image/jpeg',
            jpegQuality
          )
        })
      }

      // 4. 확장자를 .jpg 로 바꾼 파일명 생성
      function makeJpgName (originalName) {
        const idx = originalName.lastIndexOf('.')
        if (idx === -1) {
          return originalName + '.jpg'
        }
        return originalName.slice(0, idx) + '.jpg'
      }

      // ---- 메인 로직 시작 ----
      // (1) 이미지 로딩
      const imgEl = await loadImageFromFile(file)
      // (2) 축소할 크기 계산
      const { width: targetW, height: targetH } = calcTargetSize(
        imgEl.width,
        imgEl.height,
        maxSize
      )
      // (3) 캔버스에서 JPEG Blob 생성
      const jpegBlob = await imageToJpegBlob(imgEl, targetW, targetH, quality)
      // (4) Blob → File 변환
      const newName = makeJpgName(file.name)
      return new File([jpegBlob], newName, { type: 'image/jpeg' })
    },
    async onImportExcelFile (event) {
      const file = event.target.files[0]
      if (!file) {
        eventBus.$emit('alert', { active: true, text: '파일을 찾을 수 없습니다' })
        return
      }

      // 정합성 검사
      const isValid = await this.verifyExcelFile(file)
      if (!isValid) {
        eventBus.$emit('alert', { active: true, text: '엑셀 파일이 포맷에 맞지 않습니다' })
        return
      }

      // 파일로부터 데이터 읽어오기
      this.carListReadTemp = await this.readExcelFileData(file)
      this.$refs.excelFileInput.value = ''

      // 차량 전체 데이터 서버로부터 가져오기
      eventBus.$emit('showLoading')
      const allListResponse = await car.list_all() // 차량 목록
      eventBus.$emit('hideLoading')
      if (allListResponse.status !== 200) {
        eventBus.$emit('alert', { active: true, text: '데이터 로드 실패' })
        return
      }
      const carList = allListResponse.data

      // 차량 전체 데이터와 파일 데이터 비교
      this.diffDialogContent = this.compareCarListReadToOrigin(carList, this.carListReadTemp)
      this.diffDialogShow = true
    },
    async verifyExcelFile (file) {
      const buffer = await file.arrayBuffer()
      const workbook = XLSX.read(buffer, { type: 'array' })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const header = XLSX.utils.sheet_to_json(sheet, { header: 1, range: 0, blankrows: false })

      const expectedRow2 = [
        null, '차번호', '등록날짜', '이름', '전화번호', '비고',
        '시작날짜', '종료날짜', '비고',
        '시작날짜', '종료날짜', '비고',
        '시작날짜', '종료날짜', '비고', '사진1', '사진2', '사진3', '사진4', '사진5',
        '이름', '전화번호', '관계',
        '이름', '전화번호', '관계',
        '이름', '전화번호', '관계',
        '이름', '전화번호', '관계',
        '이름', '전화번호', '관계'
      ]

      const actualRow2 = header[1] || []
      return JSON.stringify(actualRow2.slice(1)) === JSON.stringify(expectedRow2.slice(1))
    },
    async readExcelFileData (file) {
      const buffer = await file.arrayBuffer()
      const workbook = XLSX.read(buffer, { type: 'array' })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, range: 2 }) // 3행부터

      return rows.map(row => {
        const pictureUrls = [row[15], row[16], row[17], row[18], row[19]].filter(Boolean)
        const pictureIds = pictureUrls.map(image.getId).filter(Boolean) // ID만 추출 + null 제거
        const car = {
          carNumber: (row[1] || '').replace(' ', ''),
          createdDate: row[2] || '',
          ownerName: row[3] || '',
          ownerPhone: row[4] || '',
          ownerDetail: row[5] || '',
          basement: {
            startDate: row[6] || '',
            endDate: row[7] || '',
            reason: row[8] || ''
          },
          newbie: {
            startDate: row[9] || '',
            endDate: row[10] || '',
            reason: row[11] || ''
          },
          black: {
            startDate: row[12] || '',
            endDate: row[13] || '',
            reason: row[14] || '',
            pictures: pictureIds
          },
          relationships: []
        }

        for (let i = 0; i < 5; i++) {
          const name = row[20 + i * 3]
          const number = row[21 + i * 3]
          const relationship = row[22 + i * 3]
          if ((name != null && name.trim() !== '') || (number != null && number.trim() !== '') || (relationship != null && relationship.trim() !== '')) {
            car.relationships.push({ name, number, relationship })
          }
        }

        return car
      }).filter(car => car.carNumber.trim() !== '')
    },
    async exportFromTemplate () {
      eventBus.$emit('showLoading')
      const allListResponse = await car.list_all() // 차량 목록
      eventBus.$emit('hideLoading')

      if (allListResponse.status !== 200) {
        eventBus.$emit('alert', { active: true, text: '데이터 로드 실패' })
        return
      }

      const carList = allListResponse.data

      // 1. 템플릿 로드
      const response = await fetch('/template_car.xlsx')
      const arrayBuffer = await response.arrayBuffer()

      // 2. 워크북 열기
      const workbook = await XlsxPopulate.fromDataAsync(arrayBuffer)
      const sheet = workbook.sheet(0)

      // 3. 데이터 삽입 (A3부터 시작)
      carList.forEach((car, index) => {
        const row = index + 3
        sheet.cell(`A${row}`).value(index + 1)
        sheet.cell(`B${row}`).value(car.carNumber || '')
        sheet.cell(`C${row}`).value(car.createdDate || '')
        sheet.cell(`D${row}`).value(car.ownerName || '')
        sheet.cell(`E${row}`).value(car.ownerPhone || '')
        sheet.cell(`F${row}`).value(car.ownerDetail || '')

        const basement = car.basement
        sheet.cell(`G${row}`).value(basement.startDate || '')
        sheet.cell(`H${row}`).value(basement.endDate || '')
        sheet.cell(`I${row}`).value(basement.reason || '')

        const newbie = car.newbie
        sheet.cell(`J${row}`).value(newbie.startDate || '')
        sheet.cell(`K${row}`).value(newbie.endDate || '')
        sheet.cell(`L${row}`).value(newbie.reason || '')

        const black = car.black
        sheet.cell(`M${row}`).value(black.startDate || '')
        sheet.cell(`N${row}`).value(black.endDate || '')
        sheet.cell(`O${row}`).value(black.reason || '');
        (black.pictures || []).forEach((url, i) => {
          let col = 'T'
          if (i === 0) { col = 'P' } else if (i === 1) { col = 'Q' } else if (i === 2) { col = 'R' } else if (i === 3) { col = 'S' }
          sheet.cell(`${col}${row}`).value(this.getImageUrl(url))
        })

        // 대리 최대 5개
        for (let i = 0; i < 5; i++) {
          const rel = car.relationships?.[i] || {}
          let col0 = 'ZZ'; let col1 = 'ZZ'; let col2 = 'ZZ'
          if (i === 0) { col0 = 'U'; col1 = 'V'; col2 = 'W' }
          if (i === 1) { col0 = 'X'; col1 = 'Y'; col2 = 'Z' }
          if (i === 2) { col0 = 'AA'; col1 = 'AB'; col2 = 'AC' }
          if (i === 3) { col0 = 'AD'; col1 = 'AE'; col2 = 'AF' }
          if (i === 4) { col0 = 'AG'; col1 = 'AH'; col2 = 'AI' }
          sheet.cell(`${col0}${row}`).value(rel.name || '')
          sheet.cell(`${col1}${row}`).value(rel.number || '')
          sheet.cell(`${col2}${row}`).value(rel.relationship || '')
        }
      })

      // 4. 다운로드 트리거
      workbook.outputAsync().then(blob => {
        const url = window.URL.createObjectURL(new Blob([blob]))
        const a = document.createElement('a')
        a.href = url
        a.download = '차량정보.xlsx'
        a.click()
        window.URL.revokeObjectURL(url)
      })
    },
    resolveCarListConflicts (newList) {
      // 차 리스트 목록
      const grouped = {}
      const mergedList = []

      // newList 에서 차 번호별로 묶기
      for (const car of newList) {
        const number = car.carNumber
        if (!number) continue
        if (!grouped[number]) grouped[number] = []
        grouped[number].push(car)
      }

      for (const carNumber in grouped) {
        const list = grouped[carNumber]
        const base = list[0]
        const merged = JSON.parse(JSON.stringify(base)) // 깊은 복사

        if (list.length > 1) {
          const others = list.slice(1)
          const baseName = (base.ownerName || '').replace(/\s/g, '')
          const basePhone = (base.ownerPhone || '').replace(/\s/g, '')

          // 1. 대리인으로 추가할 대상만 필터링 (차주 동일인 제외)
          const additionalRelationships = others
            .filter(car => !((car.ownerName || '').replace(/\s/g, '') === baseName && (base.ownerPhone || '').replace(/\s/g, '') === basePhone))
            .map(car => ({
              name: car.ownerName,
              number: car.ownerPhone || '',
              relationship: ''
            }))

          merged.relationships = [
            ...(merged.relationships || []),
            ...additionalRelationships
          ]

          const isEmptyStatus = status => !status || ((!status.startDate || status.startDate === '') && (!status.endDate || status.endDate === '') && (!status.reason || status.reason === ''))

          // 2. 상태 병합은 others 전체에서 (동일인 여부 무시)
          for (const key of ['black', 'basement', 'newbie']) {
            if (isEmptyStatus(merged[key])) {
              const filled = list.find(car => !isEmptyStatus(car[key]))
              if (filled) merged[key] = JSON.parse(JSON.stringify(filled[key]))
            }
          }
        }

        mergedList.push(merged)
      }

      return mergedList
    },
    compareCarListReadToOrigin (originList, newList) {
      const carMapOrigin = Object.fromEntries(originList.map(car => [car.carNumber, car]))
      const carMapNewRaw = {}
      const conflicts = []
      const mergedNewList = []
      const conflictCarNumbers = new Set()

      const isEmptyStatus = status => !status || (!status.startDate && !status.endDate && !status.reason)

      // 1. carNumber 기준으로 그룹화
      for (const car of newList) {
        if (!carMapNewRaw[car.carNumber]) {
          carMapNewRaw[car.carNumber] = []
        }
        carMapNewRaw[car.carNumber].push(car)
      }

      // 2. 중복 carNumber 병합 처리
      for (const carNumber in carMapNewRaw) {
        const list = carMapNewRaw[carNumber]

        if (list.length === 1) {
          mergedNewList.push(list[0])
          continue
        }

        const [base, ...others] = list
        const merged = JSON.parse(JSON.stringify(base))

        const additionalRelations = others
          .filter(c => (c.ownerName || '').replace(/\s/g, '') !== (base.ownerName || '').replace(/\s/g, '') ||
            (c.ownerPhone || '').replace(/\s/g, '') !== (base.ownerPhone || '').replace(/\s/g, ''))
          .map(c => ({ name: c.ownerName, number: c.ownerPhone, relationship: '' }))

        merged.relationships = [...(merged.relationships || []), ...additionalRelations]

        const statusLabels = {
          black: '블랙이',
          basement: '지하이용자가',
          newbie: '새가족이'
        }
        const addedStatus = []

        for (const key of ['black', 'basement', 'newbie']) {
          if (isEmptyStatus(merged[key])) {
            const filled = others.find(c => !isEmptyStatus(c[key]))
            if (filled && filled[key]) {
              merged[key] = JSON.parse(JSON.stringify(filled[key]))
              addedStatus.push(`${filled.ownerName}에 의해 ${statusLabels[key]}됨`)
            }
          }
        }

        if (merged.relationships.length > 5) {
          conflicts.push(`${carNumber} 가 둘 이상 존재하며, ${base.ownerName} 의 데이터로 합치려 했지만 대리인이 너무 많아서 짤렸습니다(최대 5인). 확인이 필요합니다`)
          conflictCarNumbers.add(carNumber)
        } else {
          mergedNewList.push(merged)
          let msg = `${carNumber} 가 둘 이상 존재하며, ${base.ownerName} 의 데이터로 합쳐졌습니다`
          if (addedStatus.length > 0) {
            msg += ` (${addedStatus.join(', ')})`
          }
          conflicts.push(msg)
          conflictCarNumbers.add(carNumber)
        }
      }

      // 3. 비교용 map 생성
      const carMapNew = {}
      for (const car of mergedNewList) {
        if (!carMapNew[car.carNumber]) {
          carMapNew[car.carNumber] = car
        }
      }

      const newCars = Object.keys(carMapNew)
        .filter(carNumber => !(carNumber in carMapOrigin) && !conflictCarNumbers.has(carNumber))
        .map(carNumber => `${carNumber}, ${carMapNew[carNumber].ownerName}`)

      const deleteCars = Object.keys(carMapOrigin)
        .filter(carNumber => !(carNumber in carMapNew) && !conflictCarNumbers.has(carNumber))
        .map(carNumber => `${carNumber}, ${carMapOrigin[carNumber].ownerName}`)

      const changeCars = []
      const normalizeObj = (obj, pictures = false) => {
        if (!obj) return { startDate: '', endDate: '', reason: '', pictures: pictures ? [] : undefined }
        if (pictures && !obj.pictures) obj.pictures = []
        return obj
      }

      for (const carNumber of Object.keys(carMapNew)) {
        if (!(carNumber in carMapOrigin)) continue
        if (conflictCarNumbers.has(carNumber)) continue

        const origin = carMapOrigin[carNumber]
        const newer = carMapNew[carNumber]
        const changes = []

        if (origin.ownerName !== newer.ownerName) {
          changes.push(`차주: ${origin.ownerName} → ${newer.ownerName}`)
        }
        if (origin.ownerPhone !== newer.ownerPhone) {
          changes.push(`차주번호: ${origin.ownerPhone} → ${newer.ownerPhone}`)
        }
        if (origin.ownerDetail !== newer.ownerDetail) {
          changes.push(`차주비고: ${origin.ownerDetail} → ${newer.ownerDetail}`)
        }

        const relOrigin = Object.fromEntries((origin.relationships || []).map(r => [r.name ?? '', { name: r.name ?? '', number: r.number ?? '', relationship: r.relationship ?? '' }]))
        const relNew = Object.fromEntries((newer.relationships || []).map(r => [r.name ?? '', { name: r.name ?? '', number: r.number ?? '', relationship: r.relationship ?? '' }]))

        for (const name of Object.keys(relNew)) {
          if (name in relOrigin) {
            const o = relOrigin[name]
            const n = relNew[name]
            if (o.number !== n.number || o.relationship !== n.relationship) {
              if (o.number !== n.number && o.relationship === n.relationship) {
                changes.push(`대리인 수정됨: ${name}, 번호: ${o.number} → ${n.number}`)
              } else if (o.number === n.number && o.relationship !== n.relationship) {
                changes.push(`대리인 수정됨: ${name}, 관계: ${o.relationship} → ${n.relationship}`)
              } else {
                changes.push(`대리인 수정됨: ${name}, 번호: ${o.number} → ${n.number}, 관계: ${o.relationship} → ${n.relationship}`)
              }
            }
          } else {
            changes.push(`대리인 추가됨: ${name}`)
          }
        }

        for (const name of Object.keys(relOrigin)) {
          if (!(name in relNew)) {
            const o = relOrigin[name]
            changes.push(`대리인 삭제됨: ${name}, ${o.number}, ${o.relationship}`)
          }
        }

        for (const key of ['black', 'basement', 'newbie']) {
          const label = key === 'black' ? '블랙' : key === 'basement' ? '지하' : '새가족'
          const o = normalizeObj(origin[key], key === 'black')
          const n = normalizeObj(newer[key], key === 'black')

          const originStartDate = (!o.startDate) ? '지정되지 않음' : o.startDate
          const originEndDate = (!o.endDate) ? '지정되지 않음' : o.endDate
          const newStartDate = (!n.startDate) ? '지정되지 않음' : n.startDate
          const newEndDate = (!n.endDate) ? '지정되지 않음' : n.endDate

          if (originStartDate !== newStartDate || originEndDate !== newEndDate) {
            changes.push(`${label} 기간이 시작: ${newStartDate}, 종료: ${newEndDate} 로 변경됨`)
          }

          if (key === 'black') {
            const picturesO = new Set(o.pictures || [])
            const picturesN = new Set(n.pictures || [])
            const added = [...picturesN].filter(p => !picturesO.has(p))
            const removed = [...picturesO].filter(p => !picturesN.has(p))
            if (added.length > 0 || removed.length > 0) {
              changes.push(`블랙 사진 추가: ${added.length}, 제거: ${removed.length}`)
            }
          }
        }

        if (changes.length > 0) {
          changeCars.push({ carNumber, changes })
        }
      }

      return {
        conflicts,
        newCars,
        deleteCars,
        changeCars
      }
    }
  }
}
</script>

<style lang="scss">
  .theme--light {
    &.left {
      /*margin-top: 70px;*/
      /*left: 256px;*/
      /*box-shadow: 2px 4px 6px 0 rgba(0, 0, 0, 0.12);*/
      &.v-navigation-drawer:not(.v-navigation-drawer--floating) {
        .v-navigation-drawer__border {
          background: none;
        }
      }
    }
  }

  .menu__item-main .menu__item-expanded {
    flex: 1;      /* 부모(.menu__item-main)가 flex-container 이므로, 이 자식이 남은 폭을 채웁니다 */
  }

  .content--has-menu {
    margin-left: 30px;
    width: calc(100% - 430px);
    max-width: calc(100% - 330px);
  }

  /* 사진 줄 */
  .photo-row {
    display: flex;
    margin-bottom: 16px;
  }
  .photo-thumb,
  .photo-add {
    position: relative;
    width: 80px;
    height: 80px;
    border: 1px solid #ccc;
    border-radius: 4px;
    margin-right: 8px;

    display: flex;
    align-items: center;
    justify-content: center;
  }
  .thumb-img {
    width: 100%;
    height: 100%;
    border-radius: 4px;
  }
  .remove-btn {
    position: absolute;
    top: -6px;
    right: -6px;
    background: #fff;
    border-radius: 50%;
  }

  /* 날짜 줄 */
  .date-row {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }
  .date-field {
    flex: 1;
  }
  .tilde {
    margin: 0 20px;
    font-size: 1.2rem;
    color: #555;
  }

  /* 사유 textarea */
  .reason-field {
    width: 100%;
  }

  .detail-section {
    border: 1px solid #ccc;
    border-radius: 8px;
    background-color: #f7f7f7;
    padding: 16px;
    margin-top: 24px;
    margin-bottom: 24px;
  }
  .detail-section legend {
    padding: 0 8px;
    margin-left: 8px;
    font-size: 1rem;
    font-weight: 600;
    color: #777;
  }

  /* 각 행을 가로로 배치 */
  .relation-row {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
  }

  /* 필드 너비 균일하게 */
  .rel-field {
    flex: 1;
    margin-right: 12px;
  }

  /* 마지막 버튼만 오른쪽에 붙도록 마진 제거 */
  .remove-btn,
  .add-btn {
    flex-shrink: 0;
  }

  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.47); /* 0x77000000 */
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
  }

  .dialog-box {
    background-color: #ffffff;
    margin: 100px 100px; /* 위아래 100px 여백 */
    min-width: 80vw; /* 최소 높이 80% */
    min-height: 80vh; /* 최소 높이 80% */
    border-radius: 8px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 200px); /* 상하 100px씩 여백 포함한 최대 높이 */
    overflow: hidden;
  }

  .dialog-title {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 8px;
  }

  .dialog-subtitle {
    font-size: 14px;
    color: #555;
    margin-bottom: 16px;
  }

  .dialog-content {
    background-color: #fdfdfd; /* 0xfffdfdfd */
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    margin-bottom: 16px;
    border-radius: 4px;
    border: 1px solid #ddd;
  }

  .change-line {
    font-size: 14px;
    margin-bottom: 6px;
    color: #333;
  }

  .dialog-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
</style>
